import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Pencil, Star } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchEarnRules, updateEarnRule, type AdminEarnRuleRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { useToast } from '@/providers/UiProviders'

/**
 * A21.03 — Ball earn qoidalari (28 §2). Config-driven: admin qiymat/cap/enabled'ni
 * deploysiz o'zgartiradi (`points_earn_rules`). Hodisalar kod-belgilangan → yangi
 * yaratilmaydi, faqat tahrirlanadi. Kill-switch = enabled=false.
 */
export function EarnRules() {
  const t = useT()
  const [target, setTarget] = useState<AdminEarnRuleRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const columns = useMemo<AdminColumn<AdminEarnRuleRow>[]>(
    () => [
      {
        key: 'event',
        header: t.points.event,
        type: 'text',
        minWidth: 180,
        render: (r) => (
          <span className="flex items-center gap-2">
            <Star size={14} className="text-accent" />
            <span className="font-mono text-sm text-primary">{r.event}</span>
          </span>
        ),
      },
      {
        key: 'amount',
        header: t.points.amount,
        type: 'number',
        width: 110,
        align: 'right',
        render: (r) => <span className="block text-right font-medium text-accent">+{r.amount}</span>,
      },
      {
        key: 'daily_cap',
        header: t.points.dailyCap,
        type: 'number',
        width: 130,
        align: 'right',
        render: (r) => <span className="block text-right text-secondary">{r.daily_cap ?? t.points.unlimited}</span>,
      },
      {
        key: 'enabled',
        header: t.points.enabled,
        type: 'text',
        width: 120,
        render: (r) => <StatusBadge status={r.enabled ? 'active' : 'deleted'} label={r.enabled ? t.points.enabled : t.points.disable} />,
      },
    ],
    [t],
  )

  const rowActions = useMemo<RowAction<AdminEarnRuleRow>[]>(
    () => [{ id: 'edit', label: t.points.edit, icon: <Pencil size={13} />, onSelect: (r) => setTarget(r) }],
    [t],
  )

  return (
    <>
      <PageHeader
        title={t.points.earnRules}
        screenId="BALL.01"
        actions={
          <div className="flex items-center gap-3">
            <Link to="/points/catalog" className="text-sm text-accent hover:underline">
              {t.points.catalog}
            </Link>
            <Link to="/points/suspicions" className="text-sm text-accent hover:underline">
              {t.points.suspicions}
            </Link>
          </div>
        }
      />
      <DataTable<AdminEarnRuleRow>
        key={reloadKey}
        screenId="BALL.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchEarnRules}
        rowId={(r) => r.event}
        rowActions={rowActions}
        emptyState={{ icon: <Star size={22} />, title: t.points.earnRules }}
      />
      {target && (
        <EarnRuleDialog
          rule={target}
          onClose={() => setTarget(null)}
          onSaved={() => {
            setTarget(null)
            setReloadKey((k) => k + 1)
          }}
        />
      )}
    </>
  )
}

function EarnRuleDialog({ rule, onClose, onSaved }: { rule: AdminEarnRuleRow; onClose: () => void; onSaved: () => void }) {
  const t = useT()
  const { toast } = useToast()
  const [amount, setAmount] = useState(String(rule.amount))
  const [cap, setCap] = useState(rule.daily_cap != null ? String(rule.daily_cap) : '')
  const [enabled, setEnabled] = useState(rule.enabled)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      await updateEarnRule(rule.event, {
        amount: parseInt(amount, 10) || 0,
        daily_cap: cap.trim() === '' ? null : parseInt(cap, 10) || 0,
        enabled,
      })
      toast(t.points.done, { tone: 'success' })
      onSaved()
    } catch (e) {
      setBusy(false)
      toast(t.points.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${t.points.edit} — ${rule.event}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t.points.cancel}
          </Button>
          <Button onClick={save} disabled={busy}>
            {t.points.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">{t.points.amount}</span>
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="numeric" autoFocus />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">
            {t.points.dailyCap} <span className="text-tertiary">({t.points.unlimited})</span>
          </span>
          <Input value={cap} onChange={(e) => setCap(e.target.value)} inputMode="numeric" placeholder="∞" />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="text-secondary">{t.points.enabled}</span>
        </label>
      </div>
    </Dialog>
  )
}
